import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row, Select, message } from 'antd';
import { withRouter } from 'react-router-dom';
import { loadHostProperties, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import UnitSettings from '../slots/UnitSettings';
import { readPageFilters, useSyncPageFilters } from './pageFilters';

const UnitConfigPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [propertyId, setPropertyId] = useState(() => readPageFilters(location && location.search).propertyId);
  const [unitId, setUnitId] = useState(() => readPageFilters(location && location.search).unitId);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);

  useEffect(() => {
    if (!hostId) {
      setProperties([]);
      return;
    }
    setPropertiesLoading(true);
    loadHostProperties(hostId)
      .then(list => {
        const propsList = list || [];
        setProperties(propsList);
        setPropertyId(prev => {
          if (prev && propsList.some(p => String(p._id) === String(prev))) return prev;
          return propsList[0] ? String(propsList[0]._id) : undefined;
        });
      })
      .catch(() => {
        setProperties([]);
        setPropertyId(undefined);
        message.error('Failed to load properties');
      })
      .finally(() => setPropertiesLoading(false));
  }, [hostId]);

  useEffect(() => {
    if (!propertyId) {
      setUnits([]);
      setUnitId(undefined);
      return;
    }
    setUnitsLoading(true);
    loadUnitsForProperty(propertyId)
      .then(list => {
        const unitList = list || [];
        setUnits(unitList);
        setUnitId(prev => {
          if (prev && unitList.some(u => String(u._id) === String(prev))) return prev;
          return unitList[0] ? String(unitList[0]._id) : undefined;
        });
      })
      .catch(() => {
        setUnits([]);
        setUnitId(undefined);
        message.error('Failed to load units');
      })
      .finally(() => setUnitsLoading(false));
  }, [propertyId]);

  useSyncPageFilters(history, location, {
    property: propertyId,
    unit: unitId,
  });

  return (
    <div>
      <Card title="Owner Reporting — Unit Config" style={{ marginBottom: 16 }}>
        <Form layout="vertical">
          <Row gutter={[16, 12]} type="flex">
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Property" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                  value={propertyId}
                  onChange={val => {
                    setPropertyId(val ? String(val) : undefined);
                    setUnitId(undefined);
                    setUnits([]);
                  }}
                  disabled={!hostId}
                  loading={propertiesLoading}
                >
                  {properties.map(p => (
                    <Select.Option key={String(p._id)} value={String(p._id)}>
                      {p.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Unit" style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  placeholder={
                    unitsLoading ? 'Loading units...' : propertyId ? 'Select unit' : 'Select property first'
                  }
                  value={unitsLoading ? undefined : unitId}
                  onChange={val => setUnitId(val ? String(val) : undefined)}
                  disabled={!propertyId || unitsLoading}
                  loading={unitsLoading}
                  notFoundContent={unitsLoading ? 'Loading...' : 'No units for this property'}
                >
                  {units.map(u => (
                    <Select.Option key={String(u._id)} value={String(u._id)}>
                      {u.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {unitId && hostId ? (
        <UnitSettings unitId={unitId} hostId={hostId} title="Unit settings" />
      ) : (
        <Card>
          <p style={{ marginBottom: 0, color: 'rgba(0,0,0,0.45)' }}>
            Select a property and unit to configure Owner Reporting settings.
          </p>
        </Card>
      )}
    </div>
  );
};

export default withRouter(UnitConfigPage);
